import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Shield,
  ShieldOff,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  BookOpen,
  MessageSquare,
  Calendar,
  Upload,
  Video,
  Library,
  RefreshCw
} from 'lucide-react';

const AdminUserAccessPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('enrolled'); // 'enrolled', 'pending', 'all', 'search'

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/override/pending-access-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (email) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/override/user-access/${encodeURIComponent(email)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
        setSelectedUser(email);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const searchUser = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    await fetchUserDetails(searchQuery.trim());
    setLoading(false);
    setActiveTab('search');
  };

  const grantQuickAccess = async (email, accessType, track = null) => {
    if (!window.confirm(`Grant ${accessType.replace('_', ' ')} access to ${email}?`)) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/override/quick-grant-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: email,
          access_type: accessType,
          track: track,
          duration_days: 365,
          reason: 'Admin granted access'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchPendingUsers();
        if (selectedUser === email) {
          fetchUserDetails(email);
        }
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail}`);
      }
    } catch (error) {
      alert('Error granting access');
    } finally {
      setProcessing(false);
    }
  };

  const setFeatureAccess = async (email, features) => {
    setProcessing(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/override/set-feature-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: email,
          features: features,
          reason: 'Admin updated feature access'
        })
      });
      
      if (response.ok) {
        alert('Feature access updated');
        fetchUserDetails(email);
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail}`);
      }
    } catch (error) {
      alert('Error updating features');
    } finally {
      setProcessing(false);
    }
  };

  const revokeAccess = async (email) => {
    if (!window.confirm(`Revoke ALL access for ${email}? This will reset them to guest status.`)) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/override/revoke-all-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: email,
          access_type: 'all',
          reason: 'Admin revoked access'
        })
      });
      
      if (response.ok) {
        alert('Access revoked');
        fetchPendingUsers();
        setUserDetails(null);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail}`);
      }
    } catch (error) {
      alert('Error revoking access');
    } finally {
      setProcessing(false);
    }
  };

  const FeatureToggle = ({ label, icon: Icon, enabled, onToggle }) => (
    <div 
      onClick={onToggle}
      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
        enabled ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center space-x-3">
        <Icon className={`w-5 h-5 ${enabled ? 'text-green-600' : 'text-gray-400'}`} />
        <span className={enabled ? 'text-green-800 font-medium' : 'text-gray-600'}>{label}</span>
      </div>
      {enabled ? (
        <CheckCircle className="w-5 h-5 text-green-600" />
      ) : (
        <XCircle className="w-5 h-5 text-gray-400" />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/content')}
            className="flex items-center text-purple-600 hover:text-purple-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Panel
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="user-access-title">
            User Access Management
          </h1>
          <p className="text-gray-600 mt-1">
            Grant, modify, or revoke access for individual users
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                placeholder="Enter user email to search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchUser()}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={searchUser}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('enrolled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'enrolled' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Enrolled Mentees ({pendingUsers.filter(u => u.is_enrolled).length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'pending' 
                ? 'bg-orange-100 text-orange-800' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Needs Access ({pendingUsers.filter(u => u.needs_attention).length})
          </button>
          <button
            onClick={() => { setActiveTab('all'); setSelectedUser(null); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'all' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Users ({pendingUsers.length})
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">
                  {activeTab === 'pending' ? 'Users Needing Attention' : 'User List'}
                </h2>
                <button onClick={fetchPendingUsers} className="text-gray-400 hover:text-gray-600">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : (
                  (activeTab === 'enrolled' 
                    ? pendingUsers.filter(u => u.is_enrolled)
                    : activeTab === 'pending' 
                      ? pendingUsers.filter(u => u.needs_attention) 
                      : pendingUsers
                  ).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                      <p>No users in this category</p>
                    </div>
                  ) : (
                    (activeTab === 'enrolled' 
                      ? pendingUsers.filter(u => u.is_enrolled)
                      : activeTab === 'pending' 
                        ? pendingUsers.filter(u => u.needs_attention) 
                        : pendingUsers
                    ).map((user) => (
                    <div
                      key={user.email}
                      onClick={() => fetchUserDetails(user.email)}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedUser === user.email ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 truncate">{user.name || 'No Name'}</span>
                        {user.is_enrolled ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                            {user.track || 'Enrolled'}
                          </span>
                        ) : user.needs_attention ? (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full">
                            Needs Access
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                            {user.current_tier}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      {user.is_enrolled && user.total_paid > 0 && (
                        <p className="text-xs text-green-600 mt-1">
                          Total paid: £{user.total_paid}
                        </p>
                      )}
                      {user.pending_payments_count > 0 && !user.is_enrolled && (
                        <p className="text-xs text-orange-600 mt-1">
                          {user.pending_payments_count} pending payment(s) - £{user.total_pending_amount}
                        </p>
                      )}
                    </div>
                  )))
                )}
              </div>
            </div>
          </div>

          {/* User Details Panel */}
          <div className="lg:col-span-2">
            {loadingDetails ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading user details...</p>
              </div>
            ) : userDetails ? (
              <div className="space-y-6">
                {/* User Info Card */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{userDetails.user?.name || 'No Name'}</h2>
                      <p className="text-gray-500">{userDetails.user?.email}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      userDetails.membership?.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {userDetails.membership?.tier || 'guest'}
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">£{userDetails.payments?.total_paid || 0}</p>
                      <p className="text-xs text-gray-500">Total Paid</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{userDetails.payments?.completed || 0}</p>
                      <p className="text-xs text-gray-500">Completed Payments</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">{userDetails.payments?.pending || 0}</p>
                      <p className="text-xs text-gray-500">Pending Payments</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => grantQuickAccess(userDetails.user.email, 'full_mentorship', 'apostolic-prophetic')}
                        disabled={processing}
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Grant Full Mentorship
                      </button>
                      <button
                        onClick={() => grantQuickAccess(userDetails.user.email, 'courses_only')}
                        disabled={processing}
                        className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                      >
                        <BookOpen className="w-4 h-4 mr-1" />
                        Courses Only
                      </button>
                      <button
                        onClick={() => grantQuickAccess(userDetails.user.email, 'library_only')}
                        disabled={processing}
                        className="px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                      >
                        <Library className="w-4 h-4 mr-1" />
                        Library Only
                      </button>
                      <button
                        onClick={() => revokeAccess(userDetails.user.email)}
                        disabled={processing}
                        className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                      >
                        <ShieldOff className="w-4 h-4 mr-1" />
                        Revoke All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Feature Access Control */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Access Control</h3>
                  <p className="text-sm text-gray-500 mb-4">Click to toggle individual features on or off</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <FeatureToggle
                      label="Community"
                      icon={MessageSquare}
                      enabled={userDetails.feature_access?.community}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        community: !userDetails.feature_access?.community
                      })}
                    />
                    <FeatureToggle
                      label="Library"
                      icon={Library}
                      enabled={userDetails.feature_access?.library}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        library: !userDetails.feature_access?.library
                      })}
                    />
                    <FeatureToggle
                      label="Courses"
                      icon={BookOpen}
                      enabled={userDetails.feature_access?.courses}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        courses: !userDetails.feature_access?.courses
                      })}
                    />
                    <FeatureToggle
                      label="Appointments"
                      icon={Calendar}
                      enabled={userDetails.feature_access?.appointments}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        appointments: !userDetails.feature_access?.appointments
                      })}
                    />
                    <FeatureToggle
                      label="File Uploads"
                      icon={Upload}
                      enabled={userDetails.feature_access?.file_uploads}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        file_uploads: !userDetails.feature_access?.file_uploads
                      })}
                    />
                    <FeatureToggle
                      label="Live Sessions"
                      icon={Video}
                      enabled={userDetails.feature_access?.live_sessions}
                      onToggle={() => setFeatureAccess(userDetails.user.email, {
                        ...userDetails.feature_access?.custom_overrides,
                        live_sessions: !userDetails.feature_access?.live_sessions
                      })}
                    />
                  </div>
                  
                  {userDetails.membership?.admin_granted && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm">
                      <p className="text-yellow-800">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        Access was granted by admin: {userDetails.membership?.admin_granted_by}
                      </p>
                    </div>
                  )}
                </div>

                {/* Payment History */}
                {userDetails.payments?.recent?.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
                    <div className="space-y-2">
                      {userDetails.payments.recent.map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{payment.package_id}</p>
                            <p className="text-xs text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">£{payment.amount}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payment.status === 'initiated' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a User</h3>
                <p className="text-gray-500">
                  Click on a user from the list or search by email to manage their access
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserAccessPage;
